import { useState, useEffect, useMemo } from 'react'
import type {
  Produto,
  Lote,
  Entidade,
  LocalArmazenamento,
  Saldo,
  Movimentacao,
  Reserva,
  Vale,
  ItemOperacional,
  SystemParameters
} from './types'
import {
  INITIAL_PRODUTOS,
  INITIAL_ENTIDADES,
  INITIAL_LOCAIS,
  INITIAL_LOTES,
  INITIAL_MOVIMENTACOES,
  INITIAL_RESERVAS,
  INITIAL_VALES,
  INITIAL_ITENS_OPERACIONAIS,
  INITIAL_PARAMETERS
} from './types'
import PCApp from './PCApp'
import MobileApp from './MobileApp'

// Import Firebase Connection
import { db, isFirebaseConfigured } from './firebase'
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  onSnapshot
} from 'firebase/firestore'

// Types for authentication and permission matrix
export interface Usuario {
  username: string
  senha?: string
  nome: string
  nivel: 'admin' | 'diretoria' | 'operador' | 'comprador'
}

export interface PermissoesMatriz {
  admin: Record<string, boolean>
  diretoria: Record<string, boolean>
  operador: Record<string, boolean>
  comprador: Record<string, boolean>
}

export const DEFAULT_PERMISSOES: PermissoesMatriz = {
  admin: {
    dashboard: true, cadastro: true, importacao: true, saida: true, putaway: true,
    locais: true, reservas: true, compras: true, balanco: true,
    almoxarifado: true, ferramentas: true, calculadora: true,
    entidades: true, parametros: true, usuarios: true
  },
  diretoria: {
    dashboard: true, cadastro: false, importacao: false, saida: false, putaway: false,
    locais: true, reservas: true, compras: true, balanco: false,
    almoxarifado: false, ferramentas: true, calculadora: true,
    entidades: true, parametros: false, usuarios: false
  },
  operador: {
    dashboard: true, cadastro: false, importacao: true, saida: true, putaway: true,
    locais: true, reservas: false, compras: false, balanco: true,
    almoxarifado: true, ferramentas: true, calculadora: true,
    entidades: false, parametros: false, usuarios: false
  },
  comprador: {
    dashboard: true, cadastro: true, importacao: false, saida: false, putaway: false,
    locais: true, reservas: true, compras: true, balanco: false,
    almoxarifado: false, ferramentas: false, calculadora: true,
    entidades: true, parametros: false, usuarios: false
  }
}

export const INITIAL_USUARIOS: Usuario[] = [
  { username: 'ADMIN', senha: '123', nome: 'Administrador Principal', nivel: 'admin' },
  { username: 'DIRETORIA', senha: '123', nome: 'Diretor Geral', nivel: 'diretoria' },
  { username: 'OPERADOR', senha: '123', nome: 'João Operador', nivel: 'operador' },
  { username: 'COMPRADOR', senha: '123', nome: 'Pedro Compras', nivel: 'comprador' }
]

export default function App() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('thoth_theme')
    return (saved as 'light' | 'dark') || 'light'
  })

  // Hash Routing State
  const [currentRoute, setCurrentRoute] = useState(() => window.location.hash)

  // Auth States
  const [usuarioLogado, setUsuarioLogado] = useState<Usuario | null>(() => {
    const saved = localStorage.getItem('thoth_usuario_logado')
    return saved ? JSON.parse(saved) : null
  })

  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [permissoes, setPermissoes] = useState<PermissoesMatriz>(DEFAULT_PERMISSOES)

  // React State Hub
  const [parametros, setParametros] = useState<SystemParameters>(INITIAL_PARAMETERS)
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [lotes, setLotes] = useState<Lote[]>([])
  const [locais, setLocais] = useState<LocalArmazenamento[]>([])
  const [movimentacoes, setMovimentacoes] = useState<Movimentacao[]>([])
  const [reservas, setReservas] = useState<Reserva[]>([])
  const [vales, setVales] = useState<Vale[]>([])
  const [entidades, setEntidades] = useState<Entidade[]>([])
  const [itensOperacionais, setItensOperacionais] = useState<ItemOperacional[]>([])

  // Watch Route hashchange
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentRoute(window.location.hash)
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  // Sync theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('thoth_theme', theme)
  }, [theme])

  // Save session to localStorage
  useEffect(() => {
    if (usuarioLogado) {
      localStorage.setItem('thoth_usuario_logado', JSON.stringify(usuarioLogado))
    } else {
      localStorage.removeItem('thoth_usuario_logado')
    }
  }, [usuarioLogado])

  // =========================================================================
  // FIREBASE SYNCHRONIZER / REALTIME LISTENERS
  // =========================================================================
  useEffect(() => {
    if (!isFirebaseConfigured) {
      // Local Storage Fallback
      const p = localStorage.getItem('thoth_produtos')
      const l = localStorage.getItem('thoth_lotes')
      const lo = localStorage.getItem('thoth_locais')
      const m = localStorage.getItem('thoth_movs')
      const r = localStorage.getItem('thoth_reservas')
      const v = localStorage.getItem('thoth_vales')
      const e = localStorage.getItem('thoth_entidades')
      const io = localStorage.getItem('thoth_itens_operacionais')
      const pa = localStorage.getItem('thoth_parametros')
      const u = localStorage.getItem('thoth_usuarios')

      const rawProds = p ? JSON.parse(p) : INITIAL_PRODUTOS
      const migratedProds = rawProds.map((x: any) => ({
        ...x,
        grupo: x.grupo || (x.categoria ? x.categoria.toUpperCase() : 'OUTROS'),
        subgrupo: x.subgrupo || 'GERAL'
      }))
      setProdutos(migratedProds)
      setLotes(l ? JSON.parse(l) : INITIAL_LOTES)
      setLocais(lo ? JSON.parse(lo) : INITIAL_LOCAIS)
      setMovimentacoes(m ? JSON.parse(m) : INITIAL_MOVIMENTACOES)
      setReservas(r ? JSON.parse(r) : INITIAL_RESERVAS)
      setVales(v ? JSON.parse(v) : INITIAL_VALES)
      setEntidades(e ? JSON.parse(e) : INITIAL_ENTIDADES)
      setItensOperacionais(io ? JSON.parse(io) : INITIAL_ITENS_OPERACIONAIS)
      let parsedParams = pa ? JSON.parse(pa) : INITIAL_PARAMETERS
      if (!parsedParams.grupos || parsedParams.grupos.length === 0) {
        parsedParams = { ...INITIAL_PARAMETERS, ...parsedParams, grupos: INITIAL_PARAMETERS.grupos }
      }
      setParametros(parsedParams)
      setUsuarios(u ? JSON.parse(u) : INITIAL_USUARIOS)
      setPermissoes(DEFAULT_PERMISSOES)
      return
    }

    // 1. Listen to Products
    const unsubProdutos = onSnapshot(collection(db, 'produtos'), (snap) => {
      const list: Produto[] = []
      snap.forEach(d => {
        const data = d.data() as any
        const grupo = data.grupo || (data.categoria ? data.categoria.toUpperCase() : 'OUTROS')
        const subgrupo = data.subgrupo || 'GERAL'
        list.push({ id: d.id, ...data, grupo, subgrupo } as Produto)
      })
      setProdutos(list)
    })

    // 2. Listen to Lotes
    const unsubLotes = onSnapshot(collection(db, 'lotes'), (snap) => {
      const list: Lote[] = []
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as Lote))
      setLotes(list)
    })

    // 3. Listen to Locais
    const unsubLocais = onSnapshot(collection(db, 'locais'), (snap) => {
      const list: LocalArmazenamento[] = []
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as LocalArmazenamento))
      setLocais(list)
    })

    // 4. Listen to Movimentacoes
    const unsubMovs = onSnapshot(collection(db, 'movimentacoes'), (snap) => {
      const list: Movimentacao[] = []
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as Movimentacao))
      setMovimentacoes(list)
    })

    // 5. Listen to Reservas
    const unsubReservas = onSnapshot(collection(db, 'reservas'), (snap) => {
      const list: Reserva[] = []
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as Reserva))
      setReservas(list)
    })

    // 6. Listen to Vales
    const unsubVales = onSnapshot(collection(db, 'vales'), (snap) => {
      const list: Vale[] = []
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as Vale))
      setVales(list)
    })

    // 7. Listen to Entidades
    const unsubEntidades = onSnapshot(collection(db, 'entidades'), (snap) => {
      const list: Entidade[] = []
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as Entidade))
      setEntidades(list)
    })

    // 8. Listen to Itens Operacionais
    const unsubItens = onSnapshot(collection(db, 'itens_operacionais'), (snap) => {
      const list: ItemOperacional[] = []
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as ItemOperacional))
      setItensOperacionais(list)
    })

    // 9. Listen to Parametros (Document 'system' in collection 'parametros')
    const unsubParametros = onSnapshot(doc(db, 'parametros', 'system'), (d) => {
      if (d.exists()) {
        const data = d.data() as SystemParameters
        if (!data.grupos || data.grupos.length === 0) {
          const merged = { ...INITIAL_PARAMETERS, ...data, grupos: INITIAL_PARAMETERS.grupos }
          setParametros(merged)
          setDoc(doc(db, 'parametros', 'system'), merged)
        } else {
          setParametros(data)
        }
      } else {
        // Seed parameters if missing
        setDoc(doc(db, 'parametros', 'system'), INITIAL_PARAMETERS)
      }
    })

    // 10. Listen to Users
    const unsubUsuarios = onSnapshot(collection(db, 'usuarios'), (snap) => {
      const list: Usuario[] = []
      snap.forEach(d => list.push({ username: d.id, ...d.data() } as Usuario))
      setUsuarios(list)
    })

    // 11. Listen to Permissoes Matrix (Document 'permissoes' in collection 'configuracoes')
    const unsubPermissoes = onSnapshot(doc(db, 'configuracoes', 'permissoes'), (d) => {
      if (d.exists()) {
        setPermissoes(d.data() as PermissoesMatriz)
      } else {
        // Seed default permissions matrix
        setDoc(doc(db, 'configuracoes', 'permissoes'), DEFAULT_PERMISSOES)
      }
    })

    // Seed database collections individually if empty
    const seedIfEmpty = (colName: string, initialData: any[], docKeyFunc: (item: any) => string, mapFunc?: (item: any) => any) => {
      getDocs(collection(db, colName)).then((snap) => {
        if (snap.empty) {
          initialData.forEach(x => {
            const key = docKeyFunc(x)
            const data = mapFunc ? mapFunc(x) : x
            setDoc(doc(db, colName, key), data)
          })
        }
      })
    }

    seedIfEmpty('produtos', INITIAL_PRODUTOS, x => x.id)
    seedIfEmpty('entidades', INITIAL_ENTIDADES, x => x.id)
    seedIfEmpty('locais', INITIAL_LOCAIS, x => x.id)
    seedIfEmpty('lotes', INITIAL_LOTES, x => x.id)
    seedIfEmpty('movimentacoes', INITIAL_MOVIMENTACOES, x => x.id)
    seedIfEmpty('reservas', INITIAL_RESERVAS, x => x.id)
    seedIfEmpty('vales', INITIAL_VALES, x => x.id)
    seedIfEmpty('itens_operacionais', INITIAL_ITENS_OPERACIONAIS, x => x.id)
    seedIfEmpty('usuarios', INITIAL_USUARIOS, x => x.username.toUpperCase(), x => ({ nome: x.nome, senha: x.senha, nivel: x.nivel }))

    return () => {
      unsubProdutos()
      unsubLotes()
      unsubLocais()
      unsubMovs()
      unsubReservas()
      unsubVales()
      unsubEntidades()
      unsubItens()
      unsubParametros()
      unsubUsuarios()
      unsubPermissoes()
    }
  }, [])

  // Local Storage Save Fallback Effect
  useEffect(() => {
    if (!isFirebaseConfigured) {
      localStorage.setItem('thoth_produtos', JSON.stringify(produtos))
      localStorage.setItem('thoth_lotes', JSON.stringify(lotes))
      localStorage.setItem('thoth_locais', JSON.stringify(locais))
      localStorage.setItem('thoth_movs', JSON.stringify(movimentacoes))
      localStorage.setItem('thoth_reservas', JSON.stringify(reservas))
      localStorage.setItem('thoth_vales', JSON.stringify(vales))
      localStorage.setItem('thoth_entidades', JSON.stringify(entidades))
      localStorage.setItem('thoth_itens_operacionais', JSON.stringify(itensOperacionais))
      localStorage.setItem('thoth_parametros', JSON.stringify(parametros))
      localStorage.setItem('thoth_usuarios', JSON.stringify(usuarios))
    }
  }, [produtos, lotes, locais, movimentacoes, reservas, vales, entidades, itensOperacionais, parametros, usuarios])

  // =========================================================================
  // SALDOS CALCULATION (Derived State from Lotes, Movs, and Reservas)
  // =========================================================================
  const saldos = useMemo(() => {
    const computedSaldos: { [key: string]: Saldo } = {}

    // Initialize all existing lots in their starting positions
    lotes.forEach(l => {
      const initMov = movimentacoes.find(m => m.lote_id === l.id && m.tipo_movimento === 'entrada')
      const initialLocal = initMov ? initMov.local_destino_id || 'local-gaiola-t1' : 'local-gaiola-t1'

      const key = `${l.produto_id}-${l.id}-${initialLocal}`
      computedSaldos[key] = {
        produto_id: l.produto_id,
        lote_id: l.id,
        local_id: initialLocal,
        quantidade_fisica: 0,
        quantidade_reservada: 0
      }
    })

    // Process movements
    movimentacoes.forEach(m => {
      // 1. Process Exit (Origin)
      if (m.tipo_movimento === 'saida' || m.tipo_movimento === 'transferencia') {
        const localOrigem = m.local_origem_id || 'local-gaiola-t1'
        const key = `${m.produto_id}-${m.lote_id}-${localOrigem}`
        if (!computedSaldos[key]) {
          computedSaldos[key] = {
            produto_id: m.produto_id,
            lote_id: m.lote_id,
            local_id: localOrigem,
            quantidade_fisica: 0,
            quantidade_reservada: 0
          }
        }
        computedSaldos[key].quantidade_fisica -= Math.abs(m.quantidade)
      }

      // 2. Process Entry (Destination)
      if (m.tipo_movimento === 'entrada' || m.tipo_movimento === 'transferencia') {
        const localDestino = m.local_destino_id || 'local-gaiola-t1'
        const key = `${m.produto_id}-${m.lote_id}-${localDestino}`
        if (!computedSaldos[key]) {
          computedSaldos[key] = {
            produto_id: m.produto_id,
            lote_id: m.lote_id,
            local_id: localDestino,
            quantidade_fisica: 0,
            quantidade_reservada: 0
          }
        }
        computedSaldos[key].quantidade_fisica += Math.abs(m.quantidade)
      }

      // 3. Process Adjustments
      if (m.tipo_movimento === 'ajuste') {
        const targetLocal = m.local_destino_id || m.local_origem_id || 'local-gaiola-t1'
        const key = `${m.produto_id}-${m.lote_id}-${targetLocal}`
        if (!computedSaldos[key]) {
          computedSaldos[key] = {
            produto_id: m.produto_id,
            lote_id: m.lote_id,
            local_id: targetLocal,
            quantidade_fisica: 0,
            quantidade_reservada: 0
          }
        }
        computedSaldos[key].quantidade_fisica += m.quantidade
      }
    })

    // Process reservations
    reservas.forEach(r => {
      if (r.status === 'reservado') {
        const matchKey = Object.keys(computedSaldos).find(key => {
          const s = computedSaldos[key]
          return s.produto_id === r.produto_id && s.lote_id === r.lote_id
        })
        if (matchKey) {
          computedSaldos[matchKey].quantidade_reservada += r.quantidade_reservada
        } else {
          const defaultKey = `${r.produto_id}-${r.lote_id}-local-gaiola-t1`
          if (!computedSaldos[defaultKey]) {
            computedSaldos[defaultKey] = {
              produto_id: r.produto_id,
              lote_id: r.lote_id,
              local_id: 'local-gaiola-t1',
              quantidade_fisica: 0,
              quantidade_reservada: 0
            }
          }
          computedSaldos[defaultKey].quantidade_reservada += r.quantidade_reservada
        }
      }
    })

    return Object.values(computedSaldos).filter(
      s => s.quantidade_fisica !== 0 || s.quantidade_reservada !== 0
    )
  }, [lotes, movimentacoes, reservas])

  // Helper functions to get balances
  const getProductPhysicalSum = (productId: string) => {
    return saldos
      .filter(s => s.produto_id === productId)
      .reduce((sum, s) => sum + s.quantidade_fisica, 0)
  }

  const getProductAvailableSum = (productId: string) => {
    return saldos
      .filter(s => s.produto_id === productId)
      .reduce((sum, s) => sum + (s.quantidade_fisica - s.quantidade_reservada), 0)
  }

  // =========================================================================
  // TRANSACTION MUTATOR HANDLERS (Saves to Firebase / Local fallback)
  // =========================================================================

  const handleAddProduct = (newProd: Omit<Produto, 'id'>) => {
    const id = `prod-${crypto.randomUUID()}`
    const productWithId: Produto = { ...newProd, id }

    if (isFirebaseConfigured) {
      setDoc(doc(db, 'produtos', id), cleanFirestoreData(productWithId))
    } else {
      setProdutos(prev => [...prev, productWithId])
    }
  }

  const handleCreateLote = (newLote: Omit<Lote, 'id' | 'data_recebimento'>, targetLocalId: string) => {
    const id = `lote-${crypto.randomUUID()}`
    const loteWithId: Lote = {
      ...newLote,
      id,
      data_recebimento: new Date().toISOString()
    }

    // Auto-update product's cost and sale price
    const newCusto = loteWithId.custo_unitario_compra
    const newVenda = loteWithId.preco_venda_calculado !== undefined 
      ? loteWithId.preco_venda_calculado 
      : Number((newCusto * 1.3).toFixed(2))

    setProdutos(prev => prev.map(p => {
      if (p.id === loteWithId.produto_id) {
        const updatedProd = {
          ...p,
          preco_compra: newCusto,
          preco_venda: newVenda
        }
        if (isFirebaseConfigured) {
          setDoc(doc(db, 'produtos', p.id), cleanFirestoreData(updatedProd))
        }
        return updatedProd
      }
      return p
    }))

    const movId = `mov-${crypto.randomUUID()}`
    const movement: Movimentacao = {
      id: movId,
      data_movimento: new Date().toISOString(),
      produto_id: newLote.produto_id,
      lote_id: id,
      local_destino_id: targetLocalId,
      tipo_movimento: 'entrada',
      motivo: 'compra',
      quantidade: newLote.quantidade_nota,
      usuario: usuarioLogado ? usuarioLogado.nome : 'Operador Recebimento'
    }

    if (isFirebaseConfigured) {
      setDoc(doc(db, 'lotes', id), cleanFirestoreData(loteWithId))
      setDoc(doc(db, 'movimentacoes', movId), cleanFirestoreData(movement))
    } else {
      setLotes(prev => [...prev, loteWithId])
      setMovimentacoes(prev => [...prev, movement])
    }
    return loteWithId
  }

  const handleCreateReserva = (newRes: Omit<Reserva, 'id' | 'status'>) => {
    const id = `res-${crypto.randomUUID()}`
    const resWithId: Reserva = {
      ...newRes,
      id,
      status: 'rascunho'
    }

    if (isFirebaseConfigured) {
      setDoc(doc(db, 'reservas', id), cleanFirestoreData(resWithId))
    } else {
      setReservas(prev => [...prev, resWithId])
    }
  }

  const handleReserveBudget = (reservaId: string) => {
    if (isFirebaseConfigured) {
      const match = reservas.find(r => r.id === reservaId)
      if (match) {
        setDoc(doc(db, 'reservas', reservaId), { ...match, status: 'reservado' })
      }
    } else {
      setReservas(prev => prev.map(r => r.id === reservaId ? { ...r, status: 'reservado' } : r))
    }
  }

  const handleTriggerSale = (reservaId: string) => {
    const res = reservas.find(r => r.id === reservaId)
    if (!res) return

    const currentBalance = saldos.find(s => s.produto_id === res.produto_id && s.lote_id === res.lote_id)
    const localId = currentBalance ? currentBalance.local_id : 'local-gaiola-t1'
    const physicalQty = currentBalance ? currentBalance.quantidade_fisica : 0

    const movId = `mov-${crypto.randomUUID()}`
    const exitMov: Movimentacao = {
      id: movId,
      data_movimento: new Date().toISOString(),
      produto_id: res.produto_id,
      lote_id: res.lote_id,
      local_origem_id: localId,
      tipo_movimento: 'saida',
      motivo: 'venda',
      quantidade: res.quantidade_reservada,
      usuario: usuarioLogado ? usuarioLogado.nome : 'Administrador PC'
    }

    const updatedReserva = { ...res, status: 'vendido' }

    if (isFirebaseConfigured) {
      setDoc(doc(db, 'reservas', reservaId), updatedReserva)
      setDoc(doc(db, 'movimentacoes', movId), exitMov)
    } else {
      setReservas(prev => prev.map(r => r.id === reservaId ? { ...r, status: 'vendido' } : r))
      setMovimentacoes(prev => [...prev, exitMov])
    }

    // Recalculate average weights for specific categories (Malha, Cola, Tecido, TNT)
    const product = produtos.find(p => p.id === res.produto_id)
    const lote = lotes.find(l => l.id === res.lote_id)
    if (product && lote) {
      if (product.grupo === 'MALHA' || product.grupo === 'COLA') {
        const avg = lote.peso_medio_recalculado || product.peso_medio_unidade || 1
        const currentUnits = physicalQty / avg
        const uRemoved = res.quantidade_reservada / avg
        const remainingWeight = physicalQty - res.quantidade_reservada
        const remainingUnits = currentUnits - uRemoved
        
        if (remainingUnits > 0.01 && remainingWeight > 0.01) {
          const newAvg = remainingWeight / remainingUnits
          const updatedLote = { ...lote, peso_medio_recalculado: newAvg }
          if (isFirebaseConfigured) {
            setDoc(doc(db, 'lotes', lote.id), updatedLote)
          } else {
            setLotes(prev => prev.map(l => l.id === lote.id ? updatedLote : l))
          }
        }
      } else if (product.grupo === 'TECIDO' || product.grupo === 'TNT') {
        const standardRolo = 50
        const avg = lote.peso_medio_recalculado || standardRolo
        const currentRolls = physicalQty / avg
        const rRemoved = Math.floor(res.quantidade_reservada / avg)
        const remainingMl = physicalQty - res.quantidade_reservada
        const remainingRolls = currentRolls - rRemoved
        
        if (remainingRolls > 0.01 && remainingMl > 0.01) {
          const newAvg = remainingMl / remainingRolls
          const updatedLote = { ...lote, peso_medio_recalculado: newAvg }
          if (isFirebaseConfigured) {
            setDoc(doc(db, 'lotes', lote.id), updatedLote)
          } else {
            setLotes(prev => prev.map(l => l.id === lote.id ? updatedLote : l))
          }
        }
      }
    }
  }

  const handleCancelReserva = (reservaId: string) => {
    if (isFirebaseConfigured) {
      const match = reservas.find(r => r.id === reservaId)
      if (match) {
        setDoc(doc(db, 'reservas', reservaId), { ...match, status: 'expirado' })
      }
    } else {
      setReservas(prev => prev.map(r => r.id === reservaId ? { ...r, status: 'expirado' } : r))
    }
  }

  const handleAddMovimentacao = (mov: Omit<Movimentacao, 'id' | 'data_movimento'>) => {
    const id = `mov-${crypto.randomUUID()}`
    const movement: Movimentacao = {
      ...mov,
      id,
      data_movimento: new Date().toISOString()
    }

    if (isFirebaseConfigured) {
      setDoc(doc(db, 'movimentacoes', id), cleanFirestoreData(movement))
    } else {
      setMovimentacoes(prev => [...prev, movement])
    }
  }

  const handleAddVale = (vale: Omit<Vale, 'id' | 'data_emprestimo'>) => {
    const id = `vale-${crypto.randomUUID()}`
    const newVale: Vale = {
      ...vale,
      id,
      data_emprestimo: new Date().toISOString()
    }

    if (isFirebaseConfigured) {
      setDoc(doc(db, 'vales', id), cleanFirestoreData(newVale))

      // Update operational stock in Firestore
      const item = itensOperacionais.find(i => i.id === vale.item_operacional_id)
      if (item) {
        const update = {
          ...item,
          quantidade_total: vale.tipo === 'venda' ? Math.max(0, item.quantidade_total - 1) : item.quantidade_total,
          quantidade_disponivel: Math.max(0, item.quantidade_disponivel - 1)
        }
        setDoc(doc(db, 'itens_operacionais', item.id), update)
      }
    } else {
      setVales(prev => [...prev, newVale])
      setItensOperacionais(prev => prev.map(item => {
        if (item.id === vale.item_operacional_id) {
          return {
            ...item,
            quantidade_total: vale.tipo === 'venda' ? Math.max(0, item.quantidade_total - 1) : item.quantidade_total,
            quantidade_disponivel: Math.max(0, item.quantidade_disponivel - 1)
          }
        }
        return item
      }))
    }
  }

  const handleDevolverVale = (valeId: string) => {
    const vale = vales.find(v => v.id === valeId)
    if (!vale) return

    const updatedVale = {
      ...vale,
      status: 'devolvido',
      data_devolucao: new Date().toISOString()
    }

    if (isFirebaseConfigured) {
      setDoc(doc(db, 'vales', valeId), updatedVale)

      const item = itensOperacionais.find(i => i.id === vale.item_operacional_id)
      if (item) {
        const update = {
          ...item,
          quantidade_disponivel: Math.min(item.quantidade_total, item.quantidade_disponivel + 1)
        }
        setDoc(doc(db, 'itens_operacionais', item.id), update)
      }
    } else {
      setVales(prev => prev.map(v => v.id === valeId ? updatedVale : v))
      setItensOperacionais(prev => prev.map(item => {
        if (item.id === vale.item_operacional_id) {
          return {
            ...item,
            quantidade_disponivel: Math.min(item.quantidade_total, item.quantidade_disponivel + 1)
          }
        }
        return item
      }))
    }
  }

  const handlePerderVale = (valeId: string) => {
    const vale = vales.find(v => v.id === valeId)
    if (!vale) return

    const updatedVale = {
      ...vale,
      status: 'perdido',
      data_devolucao: new Date().toISOString()
    }

    if (isFirebaseConfigured) {
      setDoc(doc(db, 'vales', valeId), updatedVale)

      const item = itensOperacionais.find(i => i.id === vale.item_operacional_id)
      if (item) {
        const update = {
          ...item,
          quantidade_total: Math.max(0, item.quantidade_total - 1)
        }
        setDoc(doc(db, 'itens_operacionais', item.id), update)
      }
    } else {
      setVales(prev => prev.map(v => v.id === valeId ? updatedVale : v))
      setItensOperacionais(prev => prev.map(item => {
        if (item.id === vale.item_operacional_id) {
          return {
            ...item,
            quantidade_total: Math.max(0, item.quantidade_total - 1)
          }
        }
        return item
      }))
    }
  }

  const cleanFirestoreData = (data: any): any => {
    if (data === null || data === undefined) return null
    if (Array.isArray(data)) {
      return data.map(cleanFirestoreData)
    }
    if (typeof data === 'object') {
      const clean: any = {}
      Object.keys(data).forEach(key => {
        const val = data[key]
        if (val !== undefined) {
          clean[key] = cleanFirestoreData(val)
        }
      })
      return clean
    }
    return data
  }

  // Handle direct state setters (fallbacks and propagations)
  const syncSetParametros = (newParams: SystemParameters | ((prev: SystemParameters) => SystemParameters)) => {
    setParametros(prev => {
      const computed = typeof newParams === 'function' ? newParams(prev) : newParams
      if (isFirebaseConfigured) {
        setDoc(doc(db, 'parametros', 'system'), cleanFirestoreData(computed))
      }
      return computed
    })
  }

  const syncSetEntidades = (newEntidades: Entidade[] | ((prev: Entidade[]) => Entidade[])) => {
    setEntidades(prev => {
      const computed = typeof newEntidades === 'function' ? newEntidades(prev) : newEntidades
      if (isFirebaseConfigured) {
        const added = computed.filter(x => !prev.find(p => p.id === x.id))
        const deleted = prev.filter(x => !computed.find(c => c.id === x.id))
        added.forEach(ent => setDoc(doc(db, 'entidades', ent.id), cleanFirestoreData(ent)))
        deleted.forEach(ent => deleteDoc(doc(db, 'entidades', ent.id)))
      }
      return computed
    })
  }

  const syncSetLocais = (newLocais: LocalArmazenamento[] | ((prev: LocalArmazenamento[]) => LocalArmazenamento[])) => {
    setLocais(prev => {
      const computed = typeof newLocais === 'function' ? newLocais(prev) : newLocais
      if (isFirebaseConfigured) {
        const added = computed.filter(x => !prev.find(p => p.id === x.id))
        const deleted = prev.filter(x => !computed.find(c => c.id === x.id))
        added.forEach(loc => setDoc(doc(db, 'locais', loc.id), cleanFirestoreData(loc)))
        deleted.forEach(loc => deleteDoc(doc(db, 'locais', loc.id)))
      }
      return computed
    })
  }

  const syncSetUsuarios = (newUsers: Usuario[] | ((prev: Usuario[]) => Usuario[])) => {
    setUsuarios(prev => {
      const computed = typeof newUsers === 'function' ? newUsers(prev) : newUsers
      if (isFirebaseConfigured) {
        const added = computed.filter(x => !prev.find(p => p.username === x.username))
        const deleted = prev.filter(x => !computed.find(c => c.username === x.username))
        added.forEach(usr => setDoc(doc(db, 'usuarios', usr.username.toUpperCase()), cleanFirestoreData({ nome: usr.nome, senha: usr.senha, nivel: usr.nivel })))
        deleted.forEach(usr => deleteDoc(doc(db, 'usuarios', usr.username.toUpperCase())))
      }
      return computed
    })
  }

  const syncSetPermissoes = (newPerm: PermissoesMatriz | ((prev: PermissoesMatriz) => PermissoesMatriz)) => {
    setPermissoes(prev => {
      const computed = typeof newPerm === 'function' ? newPerm(prev) : newPerm
      if (isFirebaseConfigured) {
        setDoc(doc(db, 'configuracoes', 'permissoes'), cleanFirestoreData(computed))
      }
      return computed
    })
  }

  const sharedProps = {
    produtos,
    setProdutos: (newProds: Produto[] | React.SetStateAction<Produto[]>) => {
      setProdutos(prev => {
        const computed = typeof newProds === 'function' ? (newProds as any)(prev) : newProds
        if (isFirebaseConfigured) {
          const added = computed.filter((x: Produto) => !prev.find(p => p.id === x.id))
          added.forEach((p: Produto) => setDoc(doc(db, 'produtos', p.id), cleanFirestoreData(p)))
        }
        return computed
      })
    },
    lotes,
    setLotes: (newLotes: Lote[] | React.SetStateAction<Lote[]>) => {
      setLotes(prev => {
        const computed = typeof newLotes === 'function' ? (newLotes as any)(prev) : newLotes
        const added = computed.filter((x: Lote) => !prev.find(l => l.id === x.id))
        
        if (added.length > 0) {
          setProdutos(prodPrev => prodPrev.map(p => {
            const matchingLotes = added.filter((l: Lote) => l.produto_id === p.id)
            if (matchingLotes.length > 0) {
              const latestLote = matchingLotes[matchingLotes.length - 1]
              const newCusto = latestLote.custo_unitario_compra
              const newVenda = latestLote.preco_venda_calculado !== undefined 
                ? latestLote.preco_venda_calculado 
                : Number((newCusto * 1.3).toFixed(2))

              const updatedProd = {
                ...p,
                preco_compra: newCusto,
                preco_venda: newVenda
              }

              if (isFirebaseConfigured) {
                setDoc(doc(db, 'produtos', p.id), cleanFirestoreData(updatedProd))
              }

              return updatedProd
            }
            return p
          }))
        }

        if (isFirebaseConfigured) {
          added.forEach((l: Lote) => setDoc(doc(db, 'lotes', l.id), cleanFirestoreData(l)))
        }
        return computed
      })
    },
    locais,
    setLocais: syncSetLocais,
    movimentacoes,
    setMovimentacoes: (newMovs: Movimentacao[] | React.SetStateAction<Movimentacao[]>) => {
      setMovimentacoes(prev => {
        const computed = typeof newMovs === 'function' ? (newMovs as any)(prev) : newMovs
        if (isFirebaseConfigured) {
          const added = computed.filter((x: Movimentacao) => !prev.find(m => m.id === x.id))
          added.forEach((m: Movimentacao) => setDoc(doc(db, 'movimentacoes', m.id), cleanFirestoreData(m)))
        }
        return computed
      })
    },
    reservas,
    setReservas: (newRes: Reserva[] | React.SetStateAction<Reserva[]>) => {
      setReservas(prev => {
        const computed = typeof newRes === 'function' ? (newRes as any)(prev) : newRes
        if (isFirebaseConfigured) {
          const added = computed.filter((x: Reserva) => !prev.find(r => r.id === x.id))
          added.forEach((r: Reserva) => setDoc(doc(db, 'reservas', r.id), cleanFirestoreData(r)))
        }
        return computed
      })
    },
    vales,
    setVales: (newVales: Vale[] | React.SetStateAction<Vale[]>) => {
      setVales(prev => {
        const computed = typeof newVales === 'function' ? (newVales as any)(prev) : newVales
        if (isFirebaseConfigured) {
          const added = computed.filter((x: Vale) => !prev.find(v => v.id === x.id))
          added.forEach((v: Vale) => setDoc(doc(db, 'vales', v.id), cleanFirestoreData(v)))
        }
        return computed
      })
    },
    entidades,
    setEntidades: syncSetEntidades,
    itensOperacionais,
    setItensOperacionais: (newItens: ItemOperacional[] | React.SetStateAction<ItemOperacional[]>) => {
      setItensOperacionais(prev => {
        const computed = typeof newItens === 'function' ? (newItens as any)(prev) : newItens
        if (isFirebaseConfigured) {
          const added = computed.filter((x: ItemOperacional) => !prev.find(i => i.id === x.id))
          added.forEach((i: ItemOperacional) => setDoc(doc(db, 'itens_operacionais', i.id), cleanFirestoreData(i)))
        }
        return computed
      })
    },
    saldos,
    theme,
    setTheme,
    getProductPhysicalSum,
    getProductAvailableSum,
    handleAddProduct,
    handleCreateLote,
    handleCreateReserva,
    handleReserveBudget,
    handleTriggerSale,
    handleCancelReserva,
    handleAddMovimentacao,
    handleAddVale,
    handleDevolverVale,
    handlePerderVale,
    parametros,
    setParametros: syncSetParametros,
    // Session states
    usuarioLogado,
    setUsuarioLogado,
    usuarios,
    setUsuarios: syncSetUsuarios,
    permissoes,
    setPermissoes: syncSetPermissoes,
    isOnline: isFirebaseConfigured
  }

  // RENDER SELECTION BY HASH ROUTE
  if (currentRoute === '#/mobile') {
    return <MobileApp {...sharedProps} />
  }

  return <PCApp {...sharedProps} />
}
